import { useEffect, useState, useCallback } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import {
  Box,
  Paper,
  Typography,
  TextField,
  InputAdornment,
  IconButton,
  Chip,
  CircularProgress,
  TablePagination,
  Tooltip,
  alpha,
  useTheme,
} from "@mui/material";
import {
  Search,
  Star,
  StarOff,
  Mail,
  MailOpen,
  RefreshCw,
  Building2,
  ArrowUpRight,
  ArrowDownLeft,
} from "lucide-react";
import { useTranslation } from "react-i18next";
import {
  fetchInboxThreads,
  updateThread,
  fetchInboxStats,
  triggerSync,
} from "../../../../../redux/actions/inboxAction";

const EmailList = ({ folder = "inbox" }) => {
  const { t } = useTranslation();
  const theme = useTheme();
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { threads, loading } = useSelector((state) => state.inbox);

  const [search, setSearch] = useState("");
  const [page, setPage] = useState(0);
  const [syncing, setSyncing] = useState(false);

  const loadData = useCallback(() => {
    const params = { folder, page: page + 1, page_size: 25 };
    if (search) params.search = search;
    if (folder === "starred") {
      delete params.folder;
      params.is_starred = true;
    }
    dispatch(fetchInboxThreads(params));
  }, [dispatch, folder, page, search]);

  useEffect(() => { loadData(); }, [loadData]);

  // Auto-refresh every 30s
  useEffect(() => {
    const interval = setInterval(loadData, 30000);
    return () => clearInterval(interval);
  }, [loadData]);

  const handleSync = async () => {
    setSyncing(true);
    try {
      await dispatch(triggerSync());
      loadData();
    } finally {
      setSyncing(false);
    }
  };

  const handleToggleStar = async (e, thread) => {
    e.stopPropagation();
    await dispatch(updateThread(thread.id, { is_starred: !thread.is_starred }));
    loadData();
  };

  const items = threads?.items || [];
  const total = threads?.total || 0;

  const formatTime = (dateStr) => {
    if (!dateStr) return "";
    const d = new Date(dateStr);
    const now = new Date();
    const diff = now - d;
    if (diff < 86400000 && d.getDate() === now.getDate()) {
      return d.toLocaleTimeString("fr-FR", { hour: "2-digit", minute: "2-digit" });
    }
    if (diff < 604800000) {
      return d.toLocaleDateString("fr-FR", { weekday: "short" });
    }
    return d.toLocaleDateString("fr-FR", { day: "numeric", month: "short" });
  };

  const getSenderDisplay = (thread) => {
    const participants = thread.participants || [];
    if (folder === "sent" || thread.last_direction === "outbound") {
      const to = participants.filter((p) => !p.email?.endsWith("@lawis.ai"));
      if (to.length > 0) return `To: ${to[0].name || to[0].email}`;
    }
    const from = participants.find((p) => !p.email?.endsWith("@lawis.ai"));
    return from?.name || from?.email || participants[0]?.email || "—";
  };

  return (
    <Box sx={{ height: "100%", display: "flex", flexDirection: "column" }}>
      {/* Toolbar */}
      <Box sx={{ display: "flex", gap: 1.5, mb: 2, alignItems: "center" }}>
        <TextField
          size="small"
          placeholder={t("inbox_search")}
          value={search}
          onChange={(e) => { setSearch(e.target.value); setPage(0); }}
          sx={{ width: 300, "& input": { fontSize: "0.85rem" } }}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <Search size={16} color={theme.palette.text.secondary} />
              </InputAdornment>
            ),
          }}
        />
        <Box sx={{ flex: 1 }} />
        <Tooltip title={t("inbox_sync")}>
          <IconButton size="small" onClick={handleSync} disabled={syncing}>
            {syncing ? <CircularProgress size={16} /> : <RefreshCw size={16} />}
          </IconButton>
        </Tooltip>
      </Box>

      {/* Thread list */}
      <Paper
        elevation={0}
        sx={{
          border: `1px solid ${theme.palette.divider}`, borderRadius: "12px",
          flex: 1, minHeight: 0, display: "flex", flexDirection: "column", overflow: "hidden",
        }}
      >
        {loading && items.length === 0 ? (
          <Box sx={{ display: "flex", justifyContent: "center", py: 6 }}>
            <CircularProgress size={28} />
          </Box>
        ) : items.length === 0 ? (
          <Box sx={{ display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", py: 8 }}>
            <Mail size={40} strokeWidth={1} color={theme.palette.text.disabled} />
            <Typography variant="body2" sx={{ mt: 1.5, color: theme.palette.text.disabled }}>
              {t("inbox_no_emails")}
            </Typography>
          </Box>
        ) : (
          <Box sx={{ flex: 1, overflow: "auto" }}>
            {items.map((thread) => (
              <Box
                key={thread.id}
                onClick={() => navigate(`/admin/inbox/thread/${thread.id}`)}
                sx={{
                  display: "flex", alignItems: "center", gap: 1.5,
                  px: 2, py: 1.25,
                  cursor: "pointer",
                  borderBottom: `1px solid ${theme.palette.divider}`,
                  bgcolor: thread.is_read ? "transparent" : alpha(theme.palette.primary.main, 0.03),
                  transition: "all 0.1s",
                  "&:hover": { bgcolor: alpha(theme.palette.primary.main, 0.06) },
                }}
              >
                {/* Unread dot */}
                <Box sx={{ width: 8, flexShrink: 0 }}>
                  {!thread.is_read && (
                    <Box sx={{ width: 8, height: 8, borderRadius: "50%", bgcolor: theme.palette.primary.main }} />
                  )}
                </Box>

                {/* Direction icon */}
                <Box sx={{ flexShrink: 0 }}>
                  {thread.last_direction === "outbound" ? (
                    <ArrowUpRight size={14} color={theme.palette.text.disabled} />
                  ) : (
                    <ArrowDownLeft size={14} color={theme.palette.text.secondary} />
                  )}
                </Box>

                {/* Content */}
                <Box sx={{ flex: 1, minWidth: 0 }}>
                  <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 0.25 }}>
                    <Typography
                      variant="body2"
                      sx={{
                        fontWeight: thread.is_read ? 400 : 700,
                        fontSize: "0.85rem",
                        flex: 1,
                      }}
                      noWrap
                    >
                      {getSenderDisplay(thread)}
                    </Typography>
                    {thread.message_count > 1 && (
                      <Typography variant="caption" sx={{ color: theme.palette.text.disabled, fontSize: "0.65rem", flexShrink: 0 }}>
                        ({thread.message_count})
                      </Typography>
                    )}
                    <Typography variant="caption" sx={{ color: theme.palette.text.disabled, fontSize: "0.7rem", flexShrink: 0 }}>
                      {formatTime(thread.last_message_at)}
                    </Typography>
                  </Box>
                  <Typography
                    variant="body2"
                    sx={{ fontWeight: thread.is_read ? 400 : 600, fontSize: "0.8rem", color: theme.palette.text.primary }}
                    noWrap
                  >
                    {thread.subject || "(pas de sujet)"}
                  </Typography>
                  <Typography variant="caption" sx={{ color: theme.palette.text.secondary, fontSize: "0.75rem" }} noWrap>
                    {thread.snippet}
                  </Typography>
                </Box>

                {/* Right side */}
                <Box sx={{ display: "flex", alignItems: "center", gap: 0.5, flexShrink: 0 }}>
                  {thread.organization_name && (
                    <Chip
                      icon={<Building2 size={10} />}
                      label={thread.organization_name}
                      size="small"
                      sx={{
                        height: 20, fontSize: "0.6rem", borderRadius: "6px", maxWidth: 120,
                        bgcolor: alpha(theme.palette.primary.main, 0.08),
                        color: theme.palette.primary.main,
                        "& .MuiChip-icon": { color: theme.palette.primary.main },
                      }}
                    />
                  )}
                  <IconButton
                    size="small"
                    onClick={(e) => handleToggleStar(e, thread)}
                    sx={{ p: 0.25, color: thread.is_starred ? "#f59e0b" : theme.palette.text.disabled }}
                  >
                    {thread.is_starred ? <Star size={14} fill="#f59e0b" /> : <Star size={14} />}
                  </IconButton>
                </Box>
              </Box>
            ))}
          </Box>
        )}

        {total > 25 && (
          <TablePagination
            component="div"
            count={total}
            page={page}
            onPageChange={(_, p) => setPage(p)}
            rowsPerPage={25}
            rowsPerPageOptions={[25]}
            sx={{ borderTop: `1px solid ${theme.palette.divider}`, flexShrink: 0 }}
          />
        )}
      </Paper>
    </Box>
  );
};

export default EmailList;
